/**
 * 🗼 Ashen Apex — Guild Skill Tower (Phase 1 · read-only preview)
 *
 * Players can SEE the full system but can't interact yet:
 *   • Tower state banner — Guild Lv + Apex Lv + lockstep rule
 *   • 5 Core Skills grid (Apex Might / Intellect / Ashen Defense /
 *     Solar Vitality / Phoenix Focus)
 *   • Per-skill: current player level, next-level cost, why-locked chips
 *   • Tower ladder summary (1→10 totals + cooldown ladder)
 *
 * Phase 2 will wire up upgrade/buy buttons; Phase 4 will inject stats
 * into combat. Buttons here are intentionally disabled "Coming Soon"
 * affordances so players understand the meta before it goes live.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ui/confirm-dialog";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Match the locked Hero Class color palette
const CLASS_BADGE = {
  warrior:   "bg-red-900/40 text-red-200 border-red-700/50",
  mage:      "bg-purple-900/40 text-purple-200 border-purple-700/50",
  archer:    "bg-green-900/40 text-green-200 border-green-700/50",
  ash_rogue: "bg-neutral-800/60 text-neutral-200 border-neutral-700/60",
};

function fmtChits(n) {
  return Number(n || 0).toLocaleString();
}

function fmtCooldown(secs) {
  if (!secs || secs <= 0) return "Instant";
  if (secs < 3600) return `${Math.round(secs / 60)}m`;
  const h = Math.floor(secs / 3600);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

function fmtRemaining(secs) {
  if (!secs || secs <= 0) return null;
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export default function AshenApexPage() {
  const { isAuthed, refreshUser } = useAuth();
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);   // { track, action } while an upgrade / rush is in flight
  const [tick, setTick] = useState(0);      // 1s ticker so cooldown countdown updates live

  const refresh = () => {
    axios.get(`${API}/ashen-apex/state`)
      .then((r) => setState(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Tower sealed."));
  };

  useEffect(() => {
    if (!isAuthed) return;
    let cancelled = false;
    axios.get(`${API}/ashen-apex/state`)
      .then((r) => { if (!cancelled) setState(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Tower sealed."); });
    return () => { cancelled = true; };
  }, [isAuthed]);

  // 🕒 Live 1-second ticker for cooldown labels + dynamic rush cost decay.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // 🏗️ Phase 2 handlers — leader-only.
  const doUpgrade = async (track) => {
    if (busy) return;
    setBusy({ track, action: "upgrade" });
    try {
      const r = await axios.post(`${API}/ashen-apex/upgrade`, { track });
      toast.success(
        r.data.cooldown_sec > 0
          ? `Upgrade started · Lv ${r.data.next_level} unlocks in ${fmtCooldown(r.data.cooldown_sec)}.`
          : `Upgraded to Lv ${r.data.next_level}!`
      );
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start upgrade.");
    } finally {
      setBusy(null);
    }
  };

  const doRush = async (track) => {
    if (busy) return;
    setBusy({ track, action: "rush" });
    try {
      const r = await axios.post(`${API}/ashen-apex/rush`, { track });
      toast.success(`⚡ Rushed for ${r.data.rush_cost} 🪙 OD · advanced to Lv ${r.data.next_level}.`);
      try { await refreshUser?.(); } catch { /* silent */ }
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't rush upgrade.");
    } finally {
      setBusy(null);
    }
  };

  // 🛒 Phase 3 — per-member skill purchase (uses personal Wealth).
  const [buyingSkillId, setBuyingSkillId] = useState(null);
  const doPurchase = async (skillId) => {
    if (buyingSkillId) return;
    const ok = await confirmDialog({
      title: `Purchase skill?`,
      message: `Spend personal Wealth to level up this Ashen Apex skill. The cost will be deducted and the skill will tick up by 1 level.`,
      confirmLabel: "Purchase",
      cancelLabel: "Not yet",
    });
    if (!ok) return;
    setBuyingSkillId(skillId);
    try {
      const r = await axios.post(`${API}/ashen-apex/purchase-skill`, { skill_id: skillId });
      toast.success(
        `🎓 ${r.data.skill_name} → Lv ${r.data.new_level} · +${r.data.boost_gained} stat gained.`
      );
      try { await refreshUser?.(); } catch { /* silent */ }
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't purchase skill.");
    } finally {
      setBuyingSkillId(null);
    }
  };

  if (!isAuthed) {
    return (
      <PageShell>
        <div className="text-center py-20">
          <div className="text-6xl mb-3">🗼</div>
          <h1 className="text-4xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
            THE ASHEN APEX
          </h1>
          <p className="text-neutral-400 mt-2">Sign in to view your guild's Skill Tower.</p>
        </div>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell>
        <div className="rounded-xl border border-red-700/50 bg-red-900/20 p-6 text-red-200 text-sm">
          {error}
        </div>
      </PageShell>
    );
  }

  if (!state) {
    return (
      <PageShell>
        <div className="text-center py-20 text-neutral-500 italic">Reading the ashen records…</div>
      </PageShell>
    );
  }

  const { tower, member, phase_status, is_leader } = state;
  // Base rush cost lookup by track — used by TowerTrack's dynamic price.
  const ladder = state.catalog?.tower_ladder || {};
  const nextTierGuild = ladder[String(tower.guild_level + 1)] || {};
  const nextTierApex  = ladder[String(tower.apex_level + 1)] || {};

  return (
    <PageShell>
      <BackButton />
      <TowerBanner
        tower={tower}
        isLeader={!!is_leader}
        busy={busy}
        onUpgrade={doUpgrade}
        onRush={doRush}
        nextTierGuild={nextTierGuild}
        nextTierApex={nextTierApex}
        tick={tick}
      />
      {!member.in_guild && <NoGuildBanner />}
      <SkillGrid member={member} onPurchase={doPurchase} buyingId={buyingSkillId} />
      <LadderSummary tower={tower} catalog={state.catalog} />
      <PreviewBanner phaseStatus={phase_status} />
    </PageShell>
  );
}


function BackButton() {
  return (
    <Link
      to="/guild"
      data-testid="apex-back-button"
      className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-lg border border-amber-700/40 bg-black/40 hover:bg-amber-900/30 hover:border-amber-500/70 transition group"
    >
      <span className="text-xl group-hover:-translate-x-1 transition-transform">🔙</span>
      <span className="text-xs uppercase tracking-[0.2em] font-black text-amber-200">Back to Guild Hall</span>
    </Link>
  );
}


function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0500] via-black to-[#10001a] text-amber-100" data-testid="ashen-apex-page">
      <Header />
      <main className="max-w-6xl mx-auto px-4 py-6 pb-24">
        <div className="rounded-2xl border border-amber-700/40 bg-gradient-to-br from-[#1a0500] via-black/60 to-[#10001a] p-6 mb-6 relative overflow-hidden">
          <div className="absolute -top-6 -right-6 text-[160px] opacity-10 select-none">🗼</div>
          <div className="relative">
            <Link to="/guild" className="text-[10px] uppercase tracking-[0.3em] text-amber-300/70 hover:text-amber-200" data-testid="apex-breadcrumb-guild">
              ← Birds of a Feather Guild Hall
            </Link>
            <h1 className="text-4xl md:text-5xl font-black text-amber-200 mt-1" style={{ fontFamily: "Anton, sans-serif" }}>
              🗼 THE ASHEN APEX
            </h1>
            <p className="text-amber-100/80 text-sm mt-2 max-w-2xl font-serif italic">
              The guild's skill tower. Five Phoenix-forged disciplines, ten ashen rungs each. Climb together — or never climb at all.
            </p>
          </div>
        </div>
        {children}
      </main>
      <Footer />
    </div>
  );
}


function PreviewBanner({ phaseStatus }) {
  const isLive = phaseStatus === "live" || (phaseStatus && phaseStatus.toLowerCase() === "live");
  if (isLive) {
    return (
      <div
        className="rounded-xl border-2 border-emerald-500/50 bg-emerald-500/10 p-4 mb-5 flex items-center gap-3"
        data-testid="apex-live-banner"
      >
        <div className="text-3xl">🏗️</div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300/80">Phase 2 · Guild Building Upgrades Live</div>
          <p className="text-sm text-emerald-100/90 leading-snug">
            Guild Leaders can now climb the Hall and the Ashen Apex. Pooled Guild Wealth funds the climb; ⚡ Rush burns OD to skip the cooldown (cost drops 1 OD per 15 min of cooldown burned).
          </p>
        </div>
        <span className="px-2 py-1 rounded-full bg-black/40 border border-emerald-500/50 text-emerald-300 text-[10px] font-black uppercase tracking-widest">
          {phaseStatus}
        </span>
      </div>
    );
  }
  return (
    <div
      className="rounded-xl border-2 border-amber-500/50 bg-amber-500/10 p-4 mb-5 flex items-center gap-3"
      data-testid="apex-preview-banner"
    >
      <div className="text-3xl">🔒</div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-amber-300/80">Phase 1 · Preview Only</div>
        <p className="text-sm text-amber-100/90 leading-snug">
          The tower&apos;s mechanics are <b>visible but not yet operational.</b> Guild Leader upgrades, member skill purchases, and combat stat-injection arrive in upcoming phases. You&apos;re seeing the canonical ladder.
        </p>
      </div>
      <span className="px-2 py-1 rounded-full bg-black/40 border border-amber-500/50 text-amber-300 text-[10px] font-black uppercase tracking-widest">
        {phaseStatus}
      </span>
    </div>
  );
}


function TowerBanner({ tower, isLeader, busy, onUpgrade, onRush, nextTierGuild, nextTierApex, tick }) {
  const gu = tower.guild_upgrade;
  const au = tower.apex_upgrade;
  const guildPct = Math.round((tower.guild_level / tower.max_level) * 100);
  const apexPct = Math.round((tower.apex_level / tower.max_level) * 100);
  return (
    <div className="rounded-2xl border border-amber-700/40 bg-gradient-to-br from-black/60 to-amber-900/10 p-5 mb-5" data-testid="apex-tower-banner">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <TowerTrack
          icon="🏛️"
          label="Guild Hall Level"
          current={tower.guild_level}
          max={tower.max_level}
          pct={guildPct}
          upgrade={gu}
          testIdPrefix="apex-guild"
          accent="text-amber-300"
          isLeader={isLeader}
          busy={busy}
          onUpgrade={() => onUpgrade("guild")}
          onRush={() => onRush("guild")}
          trackKey="guild"
          nextTier={nextTierGuild}
          tick={tick}
        />
        <TowerTrack
          icon="🗼"
          label="Ashen Apex Level"
          current={tower.apex_level}
          max={tower.max_level}
          pct={apexPct}
          upgrade={au}
          testIdPrefix="apex-apex"
          accent="text-amber-400"
          isLeader={isLeader}
          busy={busy}
          onUpgrade={() => onUpgrade("apex")}
          onRush={() => onRush("apex")}
          trackKey="apex"
          nextTier={nextTierApex}
          tick={tick}
        />
      </div>
      <div className="mt-4 text-[11px] text-neutral-400 italic font-serif">
        ⛓️ <b className="text-amber-300">Lockstep rule:</b> Apex level cannot exceed Guild Hall level. The Leader upgrades the Hall first — only then can the Apex match it.
      </div>
      {!isLeader && (
        <div className="mt-2 text-[11px] text-neutral-500 italic" data-testid="apex-leader-hint">
          Only the Guild Leader can upgrade the tower. Contribute Wealth to the treasury to help fund the climb.
        </div>
      )}
    </div>
  );
}


function TowerTrack({
  icon, label, current, max, pct, upgrade, testIdPrefix, accent,
  isLeader, busy, onUpgrade, onRush, trackKey, nextTier, tick,
}) {
  // Live cooldown (uses `tick` to force re-render each second)
  void tick;
  const remaining = upgrade && fmtRemaining(upgrade.cooldown_remaining_sec);
  const inProgress = !!upgrade?.in_progress;
  // 💰 Dynamic rush cost — 1 OD off per 15 min of cooldown burned (owner-canon Aug 2026).
  const baseRush = Number(nextTier?.rush_od || 0);
  const totalCooldownSec = Number(nextTier?.cooldown_sec || 0);
  const remainingSec = Number(upgrade?.cooldown_remaining_sec || 0);
  const burnedMin = Math.max(0, Math.floor((totalCooldownSec - remainingSec) / 60));
  const rushCostNow = Math.max(0, baseRush - Math.floor(burnedMin / 15));

  const busyOnThis = busy?.track === trackKey;
  const upgradeDisabled = !isLeader || upgrade?.gated || busy;
  const rushDisabled = !isLeader || !inProgress || busy;

  return (
    <div data-testid={`${testIdPrefix}-track`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-2xl">{icon}</span>
        <div className="text-[10px] uppercase tracking-widest text-amber-300/70">{label}</div>
      </div>
      <div className="text-4xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
        Lv {current} <span className="text-neutral-500 text-xl">/ {max}</span>
      </div>
      <div className="mt-2 h-1.5 bg-black/60 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-amber-500 to-amber-300" style={{ width: `${pct}%` }} />
      </div>
      {upgrade && upgrade.next_level && (
        <div className="mt-3 rounded-lg border border-neutral-800 bg-black/40 p-3 text-[12px]">
          <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1">→ Next: Lv {upgrade.next_level}</div>
          <div className={`font-black ${accent}`}>
            {fmtChits(upgrade.next_cost_chits)} 💎 <span className="text-neutral-500 font-normal">· cooldown {fmtCooldown(upgrade.next_cooldown_sec)}</span>
          </div>
          {upgrade.next_unlock && (
            <div className="text-[11px] text-amber-100/80 italic mt-1">Unlocks: {upgrade.next_unlock}</div>
          )}
          {inProgress && (
            <div className="mt-1 text-[11px] text-purple-300" data-testid={`${testIdPrefix}-cooldown`}>
              ⏳ Upgrade in progress · {remaining} remaining
            </div>
          )}
          {upgrade.gated && upgrade.gated_reason && (
            <div className="mt-1 text-[11px] text-yellow-300" data-testid={`${testIdPrefix}-gate`}>🔒 {upgrade.gated_reason}</div>
          )}
          {/* 🏗️ Phase 2 — Upgrade + Rush buttons */}
          <div className="mt-2 flex gap-2">
            <button
              onClick={onUpgrade}
              disabled={upgradeDisabled || inProgress}
              data-testid={`${testIdPrefix}-upgrade-btn`}
              className={`flex-1 px-3 py-1.5 rounded font-bold text-[11px] transition ${
                (upgradeDisabled || inProgress)
                  ? "bg-neutral-800 text-neutral-500 cursor-not-allowed"
                  : "bg-amber-600 hover:bg-amber-500 text-black"
              }`}
              title={
                !isLeader ? "Only the Guild Leader can upgrade"
                : upgrade.gated ? upgrade.gated_reason
                : inProgress ? "An upgrade is already running"
                : `Spend ${fmtChits(upgrade.next_cost_chits)} 💎 from Guild Treasury`
              }
            >
              {busyOnThis && busy?.action === "upgrade"
                ? "Upgrading…"
                : inProgress
                ? "In Progress"
                : `Upgrade · ${fmtChits(upgrade.next_cost_chits)} 💎`}
            </button>
            <button
              onClick={onRush}
              disabled={rushDisabled}
              data-testid={`${testIdPrefix}-rush-btn`}
              className={`px-3 py-1.5 rounded font-bold text-[11px] transition ${
                rushDisabled
                  ? "bg-neutral-800 text-neutral-500 cursor-not-allowed"
                  : "bg-purple-600 hover:bg-purple-500 text-white"
              }`}
              title={
                !isLeader ? "Only the Guild Leader can rush"
                : !inProgress ? "Rush is available once an upgrade is running"
                : `Skip cooldown · ${rushCostNow} 🪙 OD (base ${baseRush}, decays 1/15min)`
              }
            >
              {busyOnThis && busy?.action === "rush"
                ? "Rushing…"
                : `⚡ ${rushCostNow} OD`}
            </button>
          </div>
        </div>
      )}
      {upgrade && !upgrade.next_level && (
        <div className="mt-3 rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 text-center text-[12px] font-black text-amber-300">
          🏆 MAX LEVEL ACHIEVED
        </div>
      )}
    </div>
  );
}


function NoGuildBanner() {
  return (
    <div className="rounded-xl border border-yellow-700/50 bg-yellow-900/20 p-4 mb-5 flex items-center gap-3" data-testid="apex-no-guild">
      <div className="text-3xl">🐦</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-black text-amber-200">Not in a guild yet</div>
        <p className="text-[12px] text-amber-100/80">Join one to see the tower's progress and unlock skill purchases when Phase 2 ships.</p>
      </div>
      <Link
        to="/guild"
        data-testid="apex-find-guild-link"
        className="px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-400 text-black font-black text-[11px] uppercase tracking-widest"
      >
        Find a Guild →
      </Link>
    </div>
  );
}


function SkillGrid({ member, onPurchase, buyingId }) {
  return (
    <section className="mb-5">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-2xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
          🎓 THE FIVE DISCIPLINES
        </h2>
        <div className="h-px flex-1 bg-gradient-to-r from-amber-700/60 to-transparent" />
        <div className="text-[10px] uppercase tracking-widest text-neutral-500">
          your class · {member.hero_class || "—"} · skill cap Lv {member.skill_cap}
        </div>
      </div>
      <div className="mb-3 text-[11px] text-amber-100/80 flex flex-wrap items-center gap-3" data-testid="apex-wallet-strip">
        <span>💰 Personal Wealth: <b className="text-amber-300 font-mono tabular-nums">{(member.wealth ?? 0).toLocaleString()}</b></span>
        <span>⭐ Guild Contribution: <b className="text-purple-300 font-mono tabular-nums">{(member.guild_contribution ?? 0).toLocaleString()}</b></span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="apex-skill-grid">
        {member.skills.map((sk) => (
          <SkillCard key={sk.id} sk={sk} onPurchase={onPurchase} busy={buyingId === sk.id} />
        ))}
      </div>
    </section>
  );
}


function SkillCard({ sk, onPurchase, busy }) {
  const eligibleClasses = sk.eligible_classes || [];
  // 🌐 Universal marker — backend now returns `["all"]` for open trees;
  // legacy 4-class lists still count as "universal" for pill purposes.
  const universal = eligibleClasses.includes("all") || eligibleClasses.length >= 4;
  const canBuy = !!sk.can_purchase && !busy;
  return (
    <div
      data-testid={`apex-skill-${sk.id}`}
      className={`rounded-xl border p-4 transition ${
        sk.class_eligible
          ? "border-amber-700/50 bg-gradient-to-br from-amber-900/15 to-black/40"
          : "border-neutral-800 bg-black/40 opacity-80"
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="text-3xl">{sk.icon}</div>
          <div className="min-w-0">
            <div className="text-base font-black text-amber-200 truncate" style={{ fontFamily: "Anton, sans-serif" }}>
              {sk.name.toUpperCase()}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">
              {sk.formula} → {sk.stat_targets.join(" · ")}
            </div>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-[10px] uppercase tracking-widest text-amber-300/70">Your Level</div>
          <div className="text-2xl font-black text-amber-200">
            {sk.current_level}<span className="text-neutral-500 text-sm">/{sk.max_level}</span>
          </div>
        </div>
      </div>

      <p className="text-[12px] text-amber-100/80 italic font-serif mb-3">{sk.desc}</p>

      {/* Class chips */}
      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        <span className="text-[10px] uppercase tracking-widest text-neutral-500">
          {universal ? "Open to:" : "Locked to:"}
        </span>
        {universal ? (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border bg-amber-500/10 text-amber-300 border-amber-700/50">
            🌐 All Classes
          </span>
        ) : eligibleClasses.map((c) => (
          <span
            key={c}
            className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${CLASS_BADGE[c] || "bg-neutral-800 text-neutral-300 border-neutral-700"}`}
          >
            {c.replace("_", " ")}
          </span>
        ))}
      </div>

      {/* Current vs next preview */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="rounded bg-black/40 border border-neutral-800 p-2 text-center">
          <div className="text-[9px] uppercase tracking-widest text-neutral-500">Current Boost</div>
          <div className="text-lg font-black text-amber-200">+{sk.current_boost}</div>
        </div>
        <div className="rounded bg-black/40 border border-amber-700/40 p-2 text-center">
          <div className="text-[9px] uppercase tracking-widest text-amber-300/70">
            {sk.next_level ? `Lv ${sk.next_level} →` : "Max"}
          </div>
          <div className="text-lg font-black text-amber-300">+{sk.next_boost}</div>
        </div>
      </div>

      {/* Cost preview */}
      {sk.next_cost && (
        <div className="text-[11px] text-amber-100/85 mb-2">
          Next cost: <b className="text-amber-300">{fmtChits(sk.next_cost.chits)} 💰</b>
          <span className="text-neutral-500"> + </span>
          <b className="text-purple-300">{sk.next_cost.contribution} Contribution</b>
        </div>
      )}

      {/* Purchase blocks (why locked) */}
      {sk.purchase_blocks?.length > 0 && (
        <div className="rounded bg-black/40 border border-neutral-800 p-2 mb-2">
          <ul className="space-y-0.5">
            {sk.purchase_blocks.map((b, i) => (
              <li key={i} className="text-[10px] text-yellow-300/90">🔒 {b}</li>
            ))}
          </ul>
        </div>
      )}

      <button
        disabled={!canBuy}
        onClick={() => canBuy && onPurchase?.(sk.id)}
        data-testid={`apex-skill-buy-${sk.id}`}
        className={`w-full px-3 py-1.5 rounded font-bold text-[11px] transition ${
          canBuy
            ? "bg-amber-600 hover:bg-amber-500 text-black"
            : "bg-neutral-800 text-neutral-500 cursor-not-allowed"
        }`}
        title={
          !canBuy
            ? (sk.purchase_blocks?.[0] || "Cannot purchase")
            : `Spend ${sk.next_cost?.chits?.toLocaleString?.() ?? sk.next_cost?.chits} 💰 Wealth + ${sk.next_cost?.contribution} Contribution`
        }
      >
        {busy
          ? "Purchasing…"
          : sk.next_level
          ? `Purchase Lv ${sk.next_level}`
          : "🏆 Maxed"}
      </button>
    </div>
  );
}


function LadderSummary({ tower, catalog }) {
  const ladder = catalog?.tower_ladder || {};
  return (
    <section className="mt-8">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-2xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
          📜 TOWER LADDER · Lv 1→10
        </h2>
        <div className="h-px flex-1 bg-gradient-to-r from-amber-700/60 to-transparent" />
      </div>
      <div className="rounded-2xl border border-neutral-800 bg-black/40 p-4 overflow-x-auto" data-testid="apex-ladder-table">
        <table className="w-full text-[12px] text-amber-100/85">
          <thead className="text-[10px] uppercase tracking-widest text-amber-300/70">
            <tr className="border-b border-neutral-800">
              <th className="text-left py-2 px-2">Lv</th>
              <th className="text-right py-2 px-2">🏛️ Guild</th>
              <th className="text-right py-2 px-2">🗼 Apex</th>
              <th className="text-right py-2 px-2">Cooldown</th>
              <th className="text-left py-2 px-2 hidden md:table-cell">Unlocks</th>
            </tr>
          </thead>
          <tbody className="font-mono tabular-nums">
            {Object.entries(ladder).map(([lvl, row]) => {
              const isCurrent = Number(lvl) === tower.apex_level;
              return (
                <tr
                  key={lvl}
                  className={`border-b border-neutral-900 ${isCurrent ? "bg-amber-900/20" : ""}`}
                  data-testid={`apex-ladder-row-${lvl}`}
                >
                  <td className="py-1.5 px-2 text-amber-300 font-black">{isCurrent ? "▶ " : ""}{lvl}</td>
                  <td className="py-1.5 px-2 text-right">{fmtChits(row.guild_chits)} 🪙</td>
                  <td className="py-1.5 px-2 text-right">{fmtChits(row.apex_chits)} 🪙</td>
                  <td className="py-1.5 px-2 text-right text-neutral-400">{fmtCooldown(row.cooldown_sec)}</td>
                  <td className="py-1.5 px-2 text-amber-100/70 italic font-sans text-[11px] hidden md:table-cell">{row.unlock}</td>
                </tr>
              );
            })}
            <tr className="border-t-2 border-amber-700/40 text-amber-300 font-black">
              <td className="py-2 px-2">Σ</td>
              <td className="py-2 px-2 text-right">{fmtChits(tower.ladder_totals.guild_chits_1_to_10)} 🪙</td>
              <td className="py-2 px-2 text-right">{fmtChits(tower.ladder_totals.apex_chits_1_to_10)} 🪙</td>
              <td className="py-2 px-2 text-right text-neutral-500">~32d total</td>
              <td className="py-2 px-2 hidden md:table-cell"></td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}
